import { createSelector } from 'reselect';
import { gate } from 'hub-http/gates';
import { DEFAULT_REQUIRED_SCOPE } from 'ContentEditorUI/lib/app/constants';
import { createHasGateSelector, createHasScopeSelector, getDomainPageAttachedStylesheetAccess, getScopes } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getHasLayout } from 'ContentEditorUI/redux/selectors/templateInfoSelectors';
import { getSubcategory } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getIsSitePage, getIsLandingPage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { SUPPORTED_HUBS, SUPPORTED_HUBS_REQUIRED_SCOPES } from 'collaboration-sidebar';
// @ts-expect-error Not migrated yet
import { getHasStagingDomain } from 'ContentEditorUI/redux/selectors/contentSchemaSelectors';
import { getHasTheme } from 'ContentEditorUI/redux/selectors/themeSelectors';
import { getIsStarterPageTemplate } from 'ContentEditorUI/redux/selectors/templateInfoSelectors';
const getHasLandingPagesAccessScope = createHasScopeSelector(DEFAULT_REQUIRED_SCOPE);
const getHasSitePagesAccessScope = createHasScopeSelector('sitepages-access');
const getIsMigrationUser = createHasScopeSelector('migration-specialist');
export const getHasHidePrimaryCss = createHasGateSelector('DesignManager:IDE:hidePrimaryCss');
export const getHasPermissionsToLoadEditor = createSelector([getIsSitePage, getIsMigrationUser, getHasSitePagesAccessScope, getHasLandingPagesAccessScope], (isSitePage, isMigrationUser, hasSitePagesAccessScope, hasLandingPagesAccessScope) => {
  if (isSitePage) {
    return isMigrationUser || hasSitePagesAccessScope;
  }
  return hasLandingPagesAccessScope;
});
export const getPortalHasMembershipsAccess = createHasScopeSelector('marketing-content-restricted-write');
export const getHasLandingPagesPublishScope = createHasScopeSelector('landingpages-publish');
export const getHasSitePagesPublishScope = createHasScopeSelector('pages-publish');
export const getCanPublishPages = createSelector([getSubcategory, getHasLandingPagesPublishScope, getHasSitePagesPublishScope], (subcategory, hasLandingPagesPublishScope, hasSitePagesPublishScope) => {
  if (subcategory === 'landing_page') {
    return hasLandingPagesPublishScope;
  }
  return hasSitePagesPublishScope;
});
export const getHasLandingPagesScheduleScope = createHasScopeSelector('landingpages-schedule');
export const getHasSitePagesScheduleScope = createHasScopeSelector('pages-schedule');
export const getCanSchedule = createSelector([getSubcategory, getHasLandingPagesScheduleScope, getHasSitePagesScheduleScope], (subcategory, hasLandingPagesScheduleScope, hasSitePagesScheduleScope) => {
  if (subcategory === 'landing_page') {
    return hasLandingPagesScheduleScope;
  }
  return hasSitePagesScheduleScope;
});
const getHasLandingPagesPasswordProtection = createHasScopeSelector('landingpages-password-protection');
const getHasSitePagesPasswordProtection = createHasScopeSelector('sitepages-password-protection');
export const getHasPasswordProtectionAccess = createSelector([getIsLandingPage, getIsSitePage, getHasLandingPagesPasswordProtection, getHasSitePagesPasswordProtection], (isLandingPage, isSitePage, hasLandingPagesPasswordProtection, hasSitePagesPasswordProtection) => isLandingPage && hasLandingPagesPasswordProtection || isSitePage && hasSitePagesPasswordProtection);
const getUserHasLandingPagesManagePrivateContentPermissions = createHasScopeSelector('landingpages-private-content-manage');
const getUserHasSitePagesManagePrivateContentPermissions = createHasScopeSelector('pages-private-content-manage');
export const getUserHasManagePrivateContentAccess = createSelector([getIsLandingPage, getIsSitePage, getUserHasLandingPagesManagePrivateContentPermissions, getUserHasSitePagesManagePrivateContentPermissions], (isLandingPage, isSitePage, userHasLandingPagesManagePrivateContentPermissions, userHasSitePagesManagePrivateContentPermissions) => isLandingPage && userHasLandingPagesManagePrivateContentPermissions || isSitePage && userHasSitePagesManagePrivateContentPermissions);
export const getHasMenuWriteAccess = createHasScopeSelector('menus-write');
export const getHasHubDbCustomDomainAccess = createHasScopeSelector('hubdb-custom-domain-access');
export const getHasSocialWriteAccess = createHasScopeSelector('social-write');
export const getHasAnyStylesheetAccess = createSelector([getHasHidePrimaryCss, getHasLayout, getDomainPageAttachedStylesheetAccess], (hasHidePrimaryCss, hasLayout, hasDomainPageAttachedStylesheetAccess) => !hasHidePrimaryCss || hasLayout || hasDomainPageAttachedStylesheetAccess);
export const getHasDynamicPageAccess = createHasScopeSelector('dynamic-page-access');
export const getHasCrmObjectAccess = createHasScopeSelector('crm-object-types-read');
export const getHasCrmDynamicPageAccess = createSelector([getHasDynamicPageAccess, getHasCrmObjectAccess], (hasDynamicPageAccess, hasCrmObjectAccess) => hasDynamicPageAccess && hasCrmObjectAccess);
const getHasCmsHubCommenting = createHasScopeSelector(SUPPORTED_HUBS_REQUIRED_SCOPES[SUPPORTED_HUBS.CMS_HUB]);
const getHasMarketingHubCommenting = createHasScopeSelector(SUPPORTED_HUBS_REQUIRED_SCOPES[SUPPORTED_HUBS.MARKETING_HUB]);
export const getHasCommenting = createSelector([getIsSitePage, getHasCmsHubCommenting, getHasMarketingHubCommenting], (isSitePage, hasCmsHubCommenting, hasMarketingHubCommenting) => {
  return isSitePage ? hasCmsHubCommenting : hasCmsHubCommenting || hasMarketingHubCommenting;
});
export const getHasApprovalsReadAccess = createSelector([getIsLandingPage, getIsSitePage, getHasStagingDomain, getScopes], (isLandingPage, isSitePage, hasStagingDomain, scopes) => {
  if (!hasStagingDomain) {
    if (isLandingPage) {
      return scopes.includes('landingpages-approval-read');
    }
    if (isSitePage) {
      return scopes.includes('pages-approval-read');
    }
  }
  return false;
});
export const getHasApprovalsWriteAccess = createSelector([getIsLandingPage, getIsSitePage, getScopes], (isLandingPage, isSitePage, scopes) => {
  if (isLandingPage) {
    return scopes.includes('landingpages-approval-write');
  }
  if (isSitePage) {
    return scopes.includes('pages-approval-write');
  }
  return false;
});
export const getIsUngatedForAiAbLandingPages = createHasGateSelector('AiAb:LandingPages');
export const getIsUngatedForAiLp = createHasGateSelector('AILandingPages:MVP');
export const getHasPersonalizationAccess = createHasScopeSelector('personalize-view');
const getHasLandingPagesPersonalizationAccess = createHasScopeSelector('landing-pages-personalization-access');
const getHasSitePagesPersonalizationAccess = createHasScopeSelector('site-pages-personalization-access');
export const getHasPersonalizationVariantAccess = createSelector([getHasPersonalizationAccess, getIsLandingPage, getIsSitePage, getHasLandingPagesPersonalizationAccess, getHasSitePagesPersonalizationAccess], (hasPersonalizationAccess, isLandingPage, isSitePage, hasLandingPagesPersonalizationAccess, hasSitePagesPersonalizationAccess) => {
  if (!hasPersonalizationAccess) {
    return false;
  }
  if (isLandingPage) {
    return hasLandingPagesPersonalizationAccess;
  }
  if (isSitePage) {
    return hasSitePagesPersonalizationAccess;
  }
  return false;
});
export const getIsUngatedForUBB = createHasGateSelector(gate('HubSpotCredits'));
export const getIsUngatedForUniversalThemeEditor = createHasGateSelector(gate('UniversalThemeEditor'));
export const getCanUseUniversalThemeEditor = createSelector([getIsUngatedForUniversalThemeEditor, getHasTheme, getIsStarterPageTemplate], (isUngatedForUniversalThemeEditor, hasTheme, isStarterPageTemplate) => {
  return isUngatedForUniversalThemeEditor && hasTheme && !isStarterPageTemplate;
});
export const getHasAccessToDesignPanel = createSelector([getHasTheme, getIsUngatedForUniversalThemeEditor, getIsStarterPageTemplate], (hasTheme, isUngatedForUniversalThemeEditor, isStarterPageTemplate) => {
  return hasTheme && (isUngatedForUniversalThemeEditor || isStarterPageTemplate);
});